import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { equipmentService } from '../../../../../services';
import './Equipment.css';

const BrandAutocomplete = ({ value, onChange, categoryId, error, touched }) => {
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    // Fetch brands when category changes
    useEffect(() => {
        const loadBrands = async () => {
            if (!categoryId) {
                setOptions([]);
                return;
            }

            setIsLoading(true);
            try {
                const data = await equipmentService.getBrandsByCategory(categoryId);
                const formattedOptions = data.map(brand => ({
                    value: brand.brandId,
                    label: brand.name
                }));

                formattedOptions.push({
                    value: '__custom__',
                    label: 'Brand not listed - add it'
                });
                setOptions(formattedOptions);

                if (value) {
                    if (value === '__custom__') {
                        setSelectedOption({
                            value: '__custom__',
                            label: 'Brand not listed - add it'
                        });
                    } else {
                        const selectedBrand = data.find(b => Number(b.brandId) === Number(value));
                        if (selectedBrand) {
                            setSelectedOption({
                                value: selectedBrand.brandId,
                                label: selectedBrand.name
                            });
                        } else {
                            setSelectedOption(null);
                        }
                    }
                } else {
                    setSelectedOption(null);
                }
            } catch (err) {
                console.error('Error fetching brands:', err);
                setOptions([
                    {
                        value: '__custom__',
                        label: 'Brand not listed - add it'
                    }
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        loadBrands();
    }, [categoryId, value]);

    // Handle selection
    const handleChange = selectedOption => {
        //console.log("Selected option:", selectedOption);
        setSelectedOption(selectedOption);

        // Pass the selected value to parent
        onChange({
            target: {
                name: 'brandId',
                value: selectedOption ? selectedOption.value : ''
            }
        });
    };

    return (
        <div style={{ position: 'relative', zIndex: 50 }}>
            <Select
                value={selectedOption}
                onChange={handleChange}
                options={options}
                isSearchable={true}
                isDisabled={!categoryId || isLoading}
                isLoading={isLoading}
                placeholder={isLoading ? "Loading brands..." : "Select or search..."}
                noOptionsMessage={() => "No brands found"}
                className={touched && error ? "has-error" : ""}
                classNamePrefix="select"
                menuPortalTarget={document.body}
                styles={{
                    // Add important z-index to make dropdown appear above other elements
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: (base, state) => ({
                        ...base,
                        borderColor: touched && error ? '#dc3545' : state.isFocused ? '#4CAF50' : '#ccc',
                        boxShadow: state.isFocused ? '0 0 0 1px #4CAF50' : 'none',
                        minHeight: '44px'
                    }),
                    // More styling options...
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#4CAF50' :
                            state.isFocused ? '#f5f5f5' : 'white',
                        color: state.isSelected ? 'white' : '#333'
                    })
                }}
            />
            {touched && error && (
                <div className="error-message">{error}</div>
            )}
        </div>
    );
};

export default React.memo(BrandAutocomplete);
